import { useCallback, useRef, useState } from 'react';
import { Plus } from 'lucide-react';
import {
    ProcessingEnvironment,
    ProcessingFlow,
    Processor,
} from './hooks/useProcessing';
import { useNavigate } from 'react-router-dom';
import FlowTest from './FlowTest';
import useFlowEditor from './hooks/useFlowEditor';
import FlowStep from '../FlowStep';
import FlowTemplatePicker from './FlowTemplatePicker';
import { Button } from '@/src/shadComponents/ui/button';
import { Label } from '@/src/shadComponents/ui/label';
import { Switch } from '@/src/shadComponents/ui/switch';
import Library from '../Library/components/Library';

interface FlowEditorProps {
    flow: ProcessingFlow;
    environments: ProcessingEnvironment[];
    processors: Processor[];
    onSave: (
        environmentId: number,
        enabled: boolean,
        definition: Processor[],
    ) => void;
}

export default function FlowEditor({
    flow,
    environments,
    processors,
    onSave,
}: FlowEditorProps) {
    const [environmentId, setEnvironmentId] = useState<number>(
        flow.environmentId,
    );

    const {
        definition,
        addStep,
        removeStep,
        moveStep,
        replaceFlow,
        updateStep,
    } = useFlowEditor(flow, processors);

    const flowDefinition = definition as unknown as Processor[];

    const [feedback, setFeedback] = useState<string>('');
    const [showAssetPicker, setShowAssetPicker] = useState<boolean>(false);
    const [showTestFlowModal, setShowTestFlowModal] = useState<boolean>(false);
    const [showTemplatePicker, setShowTemplatePicker] =
        useState<boolean>(false);
    const onAssetChosen = useRef<((value: string) => void) | null>(null);

    const navigate = useNavigate();

    const [isEnabled, setIsEnabled] = useState<boolean>(flow.enabled);
    const showAssetPickerCallback = useCallback(
        (handler: (value: string) => void) => {
            onAssetChosen.current = handler;
            setShowAssetPicker(true);
        },
        [],
    );

    const currentEnvironment =
        environments.find((env) => env.id === environmentId)?.environment || '';

    return (
        <div className="mb-24">
            <div className="flex items-center gap-2 mx-4 mb-4">
                <div className="flex-1" />
                <Button onClick={() => setShowTemplatePicker(true)}>
                    Presets
                </Button>
                <Button onClick={() => setShowAssetPicker(true)}>
                    Asset Picker
                </Button>
            </div>
            {feedback && <div>{feedback}</div>}
            <div className="form">
                <div className="flex items-center gap-2 mx-4 mb-4">
                    <Label>Environment:</Label>
                    <select
                        className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                        onChange={(e) => setEnvironmentId(+e.target.value)}
                    >
                        <option value="">---</option>
                        {environments.map((environment) => {
                            return (
                                <option
                                    value={environment.id}
                                    key={environment.id}
                                    selected={environment.id === environmentId}
                                >
                                    {environment.environment}
                                </option>
                            );
                        })}
                    </select>
                </div>
                <div className="flex items-center gap-2 mb-6 mx-4">
                    <Switch
                        id="enabled"
                        checked={isEnabled}
                        onCheckedChange={() => setIsEnabled(!isEnabled)}
                    />
                    <Label htmlFor="enabled">Enabled</Label>
                </div>
                {flowDefinition.map((step, index) => {
                    return (
                        <FlowStep
                            step={step}
                            stepNumber={index}
                            totalSteps={flowDefinition.length}
                            onDelete={removeStep}
                            key={`flow-step-${JSON.stringify(step)}`}
                            moveStep={moveStep}
                            updateStep={updateStep}
                            onLaunchAssetPicker={showAssetPickerCallback}
                            currentEnvironment={currentEnvironment}
                        />
                    );
                })}
                <div className="Flow-step mx-4">
                    <Plus className="size-4 inline" />
                    Add step&nbsp;
                    <select
                        key={`processors-${flowDefinition.length}`}
                        className="h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                        onChange={(e) => addStep(e.target.value)}
                    >
                        <option value="">Select a processor</option>
                        {processors.map((processor) => {
                            return (
                                <option
                                    value={processor.name}
                                    key={processor.name}
                                >
                                    {processor.title || processor.name}
                                </option>
                            );
                        })}
                    </select>
                </div>

                <div className="mt-6 flex items-center justify-end gap-x-6 px-6 fixed bottom-0 left-0 right-0 z-50 border-t border-gray-900/10 bg-background p-3">
                    {(currentEnvironment === 'cloud' ||
                        currentEnvironment === 'text') && (
                        <Button
                            onClick={() => setShowTestFlowModal(true)}
                        >
                            Test
                        </Button>
                    )}
                    <Button
                        onClick={() => {
                            const fileName = 'flow-definition';
                            const json = JSON.stringify(definition, null, 2);
                            const blob = new Blob([json], {
                                type: 'application/json',
                            });
                            const href = URL.createObjectURL(blob);

                            const link = document.createElement('a');
                            link.href = href;
                            link.download = fileName + '.json';
                            document.body.appendChild(link);
                            link.click();

                            // clean up "a" element & remove ObjectURL
                            document.body.removeChild(link);
                            URL.revokeObjectURL(href);
                        }}
                    >
                        Export
                    </Button>
                    <Button
                        onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.json';
                            input.onchange = (e) => {
                                const file = (e.target as HTMLInputElement)
                                    .files
                                    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                      //@ts-ignore
                                      (e.target as HTMLInputElement).files[0]
                                    : null;
                                if (file) {
                                    const reader = new FileReader();
                                    reader.onload = (e) => {
                                        const text = e.target?.result;
                                        if (typeof text === 'string') {
                                            const flow = JSON.parse(text);
                                            replaceFlow(flow);
                                        }
                                    };
                                    reader.readAsText(file);
                                }
                            };
                            input.click();
                        }}
                    >
                        Import
                    </Button>

                    <div className="flex-1" />
                    <Button
                        variant="ghost"
                        type="button"
                        onClick={() => navigate(-1)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        onClick={() =>
                            onSave(environmentId, isEnabled, flowDefinition)
                        }
                    >
                        Save
                    </Button>
                </div>
            </div>
            {showAssetPicker && (
                <div
                    className="fixed inset-0 bg-black/50 bg-opacity-80 flex justify-center items-center z-50"
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowAssetPicker(false);
                    }}
                >
                    <Library
                        onImageSelected={(value: string) => {
                            navigator.clipboard.writeText(value);
                            onAssetChosen.current &&
                                onAssetChosen.current(value);

                            setShowAssetPicker(false);
                            setFeedback('Asset URL Copied to clipboard');

                            setTimeout(() => {
                                setFeedback('');
                            }, 5000);
                        }}
                        onClose={(e: any) => {
                            e.stopPropagation();
                            setShowAssetPicker(false);
                        }}
                    />
                </div>
            )}
            {showTestFlowModal && (
                <FlowTest
                    flowDefinition={definition}
                    onClose={() => setShowTestFlowModal(false)}
                />
            )}
            {showTemplatePicker && (
                <FlowTemplatePicker
                    onClose={() => setShowTemplatePicker(false)}
                    onSelect={(definition: Processor[]) => {
                        replaceFlow(definition);
                        setShowTemplatePicker(false);
                    }}
                />
            )}
        </div>
    );
}
